import type React from "react";
import { type ReactNode, useState } from "react";

import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleQuestionMarkIcon,
} from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import { belowComputer, computerUp, mobileOnly } from "simplydo/react/theme";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

const TEXT = "#111418";
const MUTED = "#5b6470";
const BORDER = "#e3e6ea";
const SURFACE = "#f5f6f8";

export const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: ${TEXT};
  font-size: 14px;

  ${belowComputer} {
    padding: 16px;
  }

  ${computerUp} {
    padding: 24px 32px;
  }
`;

export const RowFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;

  ${mobileOnly} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 28px;
    line-height: 1.15;
    font-weight: 650;
    letter-spacing: -0.02em;
  }

  .meta {
    font-size: 14px;
    color: ${MUTED};
  }
`;

const StyledInfoContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
  max-width: 100%;
  min-width: 0;

  ${belowComputer} {
    flex-direction: column;
  }

  ${computerUp} {
    flex-direction: row;
  }

  &.is-wrapped {
    flex-flow: row wrap;
    gap: 8px 16px;
  }

  &.is-sectioned {
    margin-top: 4px;
  }
`;

type InfoContainerProps = React.ComponentProps<typeof StyledInfoContainer> & {
  $wrapped?: boolean;
  $sectioned?: boolean;
  $compact?: boolean;
};

export const InfoContainer = ({
  $wrapped,
  $sectioned,
  className,
  ...props
}: InfoContainerProps) => (
  <StyledInfoContainer
    className={cx(
      $wrapped && "is-wrapped",
      $sectioned && "is-sectioned",
      className,
    )}
    {...props}
  />
);

const StyledInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 100%;
  min-width: 0;

  ${mobileOnly} {
    width: 100%;
  }

  &.is-wrapped {
    flex: 0.5;
  }

  &:not(.is-wrapped) {
    flex: 1;
  }

  > h1 {
    margin: 0;
    font-size: 18px;
    font-weight: 650;
  }

  .meta {
    color: ${MUTED};
  }

  .boxes {
    display: flex;
    gap: 16px;

    ${mobileOnly} {
      flex-direction: column;
    }
  }

  .attribution {
    font-size: 12px;
    color: ${MUTED};

    > a {
      color: inherit;
      font-weight: 600;
      text-decoration-line: underline;
    }
  }
`;

type InfoBoxProps = React.ComponentProps<typeof StyledInfoBox> & {
  $wrapped?: boolean;
};

export const InfoBox = ({ $wrapped, className, ...props }: InfoBoxProps) => (
  <StyledInfoBox
    className={cx($wrapped && "is-wrapped", className)}
    {...props}
  />
);

const StyledInfoItem = styled.a`
  display: block;
  min-width: 0;
  color: inherit;

  ${belowComputer} {
    flex: 0 100%;
  }

  ${computerUp} {
    &.is-compact {
      flex: 0 calc(33.33% - 11px);
    }

    &:not(.is-compact) {
      flex: 0 calc(50% - 8px);
    }
  }
`;

type InfoItemProps = React.ComponentProps<typeof StyledInfoItem> & {
  $compact?: boolean;
};

export const InfoItem = ({ $compact, className, ...props }: InfoItemProps) => (
  <StyledInfoItem
    className={cx($compact && "is-compact", className)}
    target="_blank"
    rel="noopener noreferrer"
    {...props}
  />
);

const StyledInfoBoxWidget = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
  max-width: 100%;
  min-width: 0;

  ${mobileOnly} {
    width: 100%;
  }

  &.has-box {
    background: white;
    border: 1px solid ${BORDER};
    border-radius: 10px;
    padding: 20px;

    ${mobileOnly} {
      padding: 16px;
    }
  }

  &.is-relaxed {
    padding: 0;
  }

  p {
    margin: 0;
    line-height: 1.5;
  }

  .attribution {
    font-size: 12px;
    color: ${MUTED};

    > a {
      color: inherit;
      font-weight: 600;
      text-decoration-line: underline;
    }
  }
`;

type InfoBoxWidgetProps = React.ComponentProps<typeof StyledInfoBoxWidget> & {
  $noBox?: boolean;
  $relaxed?: boolean;
  $sectioned?: boolean;
  $boxed?: boolean;
};

export const InfoBoxWidget = ({
  $noBox,
  $relaxed,
  $sectioned,
  $boxed,
  className,
  ...props
}: InfoBoxWidgetProps) => (
  <StyledInfoBoxWidget
    className={cx(
      (!$noBox || $boxed) && "has-box",
      $relaxed && "is-relaxed",
      $sectioned && "is-sectioned",
      className,
    )}
    {...props}
  />
);

export const InfoBoxWidgetTitleArea = styled.div<{
  $alignItems?: string;
  $noMargin?: boolean;
  $flexDirection?: string;
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: ${({ $alignItems }) => $alignItems || "center"};
  flex-direction: ${({ $flexDirection }) => $flexDirection || "row"};
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 17px;
    line-height: 1.3;
    font-weight: 650;
    color: ${TEXT};
  }

  span {
    display: block;
    font-size: 13px;
    color: ${MUTED};
  }

  > svg {
    flex: none;
    width: 18px;
    height: 18px;
    color: ${MUTED};
  }
`;

export const MetaContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${MUTED};

  a {
    color: inherit;
    text-decoration-line: underline;
  }
`;

const StyledKeyFact = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 0 1 calc(50% - 8px);
  min-width: 0;

  &.is-clickable {
    cursor: pointer;
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 10px;
  }

  .label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${MUTED};
  }

  .fact {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 15px;
    color: ${TEXT};

    &.is-underlined {
      text-decoration-line: underline;
      font-weight: 600;
    }
  }

  .info {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: ${MUTED};
    text-decoration-line: underline;
    cursor: help;
  }
`;

const StyledKeyFactLink = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  margin: 0 -10px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background-color: ${SURFACE};
  }

  > .image-container {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;

    img {
      width: 40px;
      height: 40px;
      object-fit: contain;
      border-radius: 6px;
    }
  }

  > .sub-container {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .fake-link {
    font-weight: 600;
    color: ${TEXT};
  }

  .subtitle {
    color: ${TEXT};
  }

  .fact {
    font-size: 12px;
    color: ${MUTED};
  }
`;

export const KeyFactLinkSeeMore = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  > a,
  > span {
    font-size: 13px;
    font-weight: 600;
    color: ${MUTED};
    text-decoration-line: underline;
    cursor: pointer;
  }
`;

type KeyFactProps = {
  title: string | ReactNode;
  fact: string | ReactNode;
  info?: string;
  verified?: boolean;
  notVerified?: boolean;
  noEllipsis?: boolean;
  underlined?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
};

export const KeyFact = ({
  title,
  fact,
  info,
  verified,
  notVerified,
  underlined,
  onClick,
  style,
}: KeyFactProps) => (
  <StyledKeyFact
    className={cx(onClick && "is-clickable")}
    onClick={onClick}
    style={style}
  >
    <span className="heading">
      <span className="label">{title}</span>
      {info ? (
        <Popup
          trigger={
            <span className="info">
              <Icon icon={CircleQuestionMarkIcon} />
              What is this?
            </span>
          }
        >
          {info}
        </Popup>
      ) : null}
    </span>
    <span className={cx("fact", underlined && "is-underlined")}>
      {verified || notVerified ? (
        <Icon
          icon={verified ? CircleCheckIcon : CircleAlertIcon}
          color={verified ? "green" : "orange"}
        />
      ) : null}
      {fact}
    </span>
  </StyledKeyFact>
);

type KeyFactLinkProps = {
  title: string;
  subtitle?: string;
  fact: string;
  url?: string;
  image?: string;
  imageAltContent?: ReactNode;
  style?: React.CSSProperties;
};

export const KeyFactLink = ({
  title,
  subtitle,
  fact,
  url,
  image,
  imageAltContent,
  ...rest
}: KeyFactLinkProps) => {
  const [imageDidError, setImageDidError] = useState(false);
  return (
    <StyledKeyFactLink
      onClick={() => (url ? window.open(url, "_blank") : {})}
      {...rest}
    >
      {image ? (
        <div className="image-container">
          {!imageDidError || !imageAltContent ? (
            <img onError={() => setImageDidError(true)} src={image} alt="" />
          ) : (
            imageAltContent
          )}
        </div>
      ) : null}
      <div className="sub-container">
        <span className="fake-link">{title}</span>
        {subtitle ? <span className="subtitle">{subtitle}</span> : null}
        <span className="fact">{fact}</span>
      </div>
    </StyledKeyFactLink>
  );
};
